import SockJS from 'sockjs-client';
import { over } from 'stompjs';
import { store } from 'shared/RootReducer';
import { websocketActions } from '../redux/reducers/websocket/WebSocketReducer';

class WebSocketService {
    constructor() {
        this.stompClients = {};     // roomId별 STOMP 클라이언트
        this.subscriptions = {};    // roomId별 구독 정보
        this.reconnectTimeouts = {}; // roomId별 재연결 타임아웃
        this.connectionIds = {};    // roomId별 연결 ID
        this.messageHandlers = {};  // roomId별 메시지 핸들러
        this.sendMessageIdMap = new Map(); // 메시지 중복 방지용 Map
    }

    // 메시지 핸들러 등록
    registerMessageHandler(roomId, handler) {
        if (typeof handler === 'function') {
            this.messageHandlers[roomId] = handler;
            console.log(`${roomId}의 메시지 핸들러 등록됨`);
            return true;
        }
        return false;
    }

    // 메시지 핸들러 제거
    unregisterMessageHandler(roomId) {
        if (this.messageHandlers[roomId]) {
            delete this.messageHandlers[roomId];
            console.log(`${roomId}의 메시지 핸들러 제거됨`);
            return true;
        }
        return false;
    }

    // 웹소켓 연결
    connect(roomId) {
        if (this.isConnected(roomId)) {
            console.log(`이미 ${roomId}에 연결되어 있습니다.`);
            return Promise.resolve(this.stompClients[roomId]);
        }

        store.dispatch(websocketActions.connectStart({ roomId }));

        const connectionId = Date.now();
        this.connectionIds[roomId] = connectionId;

        return new Promise((resolve, reject) => {
            try {
                const socketUrl = `${process.env.REACT_APP_ORIGIN}api/ws`;
                const socket = new SockJS(socketUrl);
                const client = over(socket);

                // 개발 환경에서만 디버그 로그 활성화
                if (process.env.NODE_ENV !== 'production') {
                    client.debug = (str) => {
                        if (str.includes('<<heartbeat') || str.includes('>>CONNECT')) {
                            return; // 하트비트 로그는 필터링
                        }
                        console.log(`[WebSocket ${roomId}]`, str);
                    };
                } else {
                    client.debug = null;
                }

                this.stompClients[roomId] = client;

                client.connect(
                    {},
                    // 연결 성공 시
                    () => {
                        // 연결 ID가 변경되었으면 무시 (이전 연결)
                        if (this.connectionIds[roomId] !== connectionId) {
                            this.disconnect(roomId, false);
                            reject(new Error("Connection obsolete"));
                            return;
                        }

                        console.log(`${roomId}에 연결 성공`);
                        store.dispatch(websocketActions.connectSuccess({ roomId }));

                        // 채널 구독
                        this.subscribeToChannel(roomId, `/chatroom/${roomId}`);

                        resolve(client);
                    },
                    // 연결 오류 시
                    (error) => {
                        console.error(`${roomId} 연결 오류:`, error);
                        store.dispatch(websocketActions.connectFail({
                            roomId,
                            error: error.toString()
                        }));

                        // 재연결 시도
                        this.scheduleReconnect(roomId);
                        reject(error);
                    }
                );
            } catch (error) {
                console.error(`${roomId} 초기화 오류:`, error);
                store.dispatch(websocketActions.connectFail({
                    roomId,
                    error: error.toString()
                }));
                reject(error);
            }
        });
    }

    // 채널 구독
    subscribeToChannel(roomId, channel) {
        if (!this.isConnected(roomId)) {
            console.error(`${roomId}에 연결되어 있지 않습니다.`);
            return false;
        }

        try {
            if (!this.subscriptions[roomId]) {
                this.subscriptions[roomId] = {};
            }

            // 이미 구독 중인 경우
            if (this.subscriptions[roomId][channel]) {
                return true;
            }

            const subscription = this.stompClients[roomId].subscribe(
                channel,
                (message) => {
                    try {
                        const payload = JSON.parse(message.body);

                        // Redux 상태 업데이트
                        store.dispatch(websocketActions.messageReceived({
                            roomId,
                            message: payload
                        }));

                        // 등록된 메시지 핸들러 호출
                        if (this.messageHandlers[roomId]) {
                            this.messageHandlers[roomId](message);
                        }
                    } catch (error) {
                        console.error(`메시지 처리 오류:`, error);
                    }
                }
            );

            this.subscriptions[roomId][channel] = subscription;
            this.stompClients[roomId].subscriptionId = subscription.id; // 클라이언트에 구독 ID 저장

            store.dispatch(websocketActions.subscribeChannel({ roomId, channel }));

            console.log(`${roomId}의 ${channel} 채널 구독 성공`);
            return true;
        } catch (error) {
            console.error(`${roomId}의 ${channel} 구독 오류:`, error);
            return false;
        }
    }

    // 메시지 전송 (중복 방지 포함)
    sendMessageWithDeduplication(roomId, message) {
        // 간단한 메시지 ID 생성 (내용 + 시간)
        const messageContent = JSON.stringify(message);
        const timestamp = Math.floor(Date.now() / 5000); // 5초 단위로 반올림
        const messageId = `${messageContent.length}-${message.code}-${timestamp}`;

        // 최근 전송 여부 확인 (5초 이내)
        const now = Date.now();

        if (this.sendMessageIdMap.has(messageId)) {
            const timeSent = this.sendMessageIdMap.get(messageId);
            if (now - timeSent < 5000) { // 5초 이내 전송된 동일 메시지
                console.log(`중복 메시지 감지 (ID: ${messageId}). 전송 건너뛰기.`);
                return Promise.resolve(false);
            }
        }

        // 메시지 전송 시간 기록
        this.sendMessageIdMap.set(messageId, now);

        // 30초 이상 지난 메시지 ID 정리
        for (const [id, time] of this.sendMessageIdMap.entries()) {
            if (now - time > 30000) {
                this.sendMessageIdMap.delete(id);
            }
        }

        console.log(`메시지 전송 (ID: ${messageId}): ${message.code}`);

        // 메시지에 roomId가 없으면 추가
        if (!message.roomId && roomId) {
            message.roomId = roomId;
        }

        // 메시지 전송 로직 호출
        return this.sendMessage(roomId, message);
    }

    // 메시지 전송
    async sendMessage(roomId, message) {
        let client = this.stompClients[roomId];

        // stompClient가 없거나 연결되지 않은 경우 재연결 시도
        if (!client || !client.connected) {
            console.log("WebSocket 연결되지 않음, 재연결 시도 중...");
            try {
                // 비동기 연결 완료 대기
                client = await this.connect(roomId);
                console.log("재연결 성공, 메시지 전송 계속");

                // 구독 상태 확인 및 필요시 재구독
                if (client && !client.subscriptionId) {
                    const currentChannel = `/chatroom/${roomId}`;
                    console.log("구독 정보 없음, 재구독 시도");
                    this.subscribeToChannel(roomId, currentChannel);
                }
            } catch (error) {
                console.log(`재연결 실패: ${error.message}`);
                throw error; // 오류 전파
            }
        }

        // 연결이 확보된 후 메시지 전송
        try {
            console.log(`메시지 타입: ${message.code}`);

            // 메시지 타입에 따라 다른 엔드포인트로 전송
            switch (message.code) {
                case "dispense":
                    client.send(`/app/dispense/${roomId}`, {}, JSON.stringify(message));
                    console.log("배출 요청 메시지 전송 완료");
                    break;
                case "railCompleted":
                    client.send(`/app/railCompleted/${roomId}`, {}, JSON.stringify(message));
                    console.log("레일 완료 메시지 전송 완료");
                    break;
                case "dispenseResult":
                    console.log(`배출 결과 메시지 전송 - 성공: ${message.success}, 디바이스: ${message.deviceCode}`);
                    client.send(`/app/dispenseResult/${roomId}`, {}, JSON.stringify(message));
                    console.log("배출 결과 메시지 전송 완료");
                    break;
                default:
                    // 기존 방식 유지 (일반 메시지)
                    client.send(`/app/sendMessage/${roomId}`, {}, JSON.stringify(message));
                    console.log("일반 메시지 전송 완료");
                    break;
            }

            return true; // 성공 시 true 반환
        } catch (error) {
            console.error(`메시지 전송 중 오류: ${error.message}`);
            throw error; // 오류 전파
        }
    }

    // 연결 해제
    disconnect(roomId, dispatch = true) {
        if (!this.stompClients[roomId]) {
            return;
        }

        if (dispatch) {
            store.dispatch(websocketActions.disconnectStart({ roomId }));
        }

        // 메시지 핸들러 제거
        this.unregisterMessageHandler(roomId);

        // 구독 해제
        if (this.subscriptions[roomId]) {
            Object.values(this.subscriptions[roomId]).forEach(subscription => {
                try {
                    subscription.unsubscribe();
                } catch (e) {
                    // 무시
                }
            });
            delete this.subscriptions[roomId];
        }

        // 연결 종료
        try {
            if (this.stompClients[roomId].connected) {
                this.stompClients[roomId].disconnect(() => {
                    if (dispatch) {
                        store.dispatch(websocketActions.disconnectSuccess({ roomId }));
                    }
                    console.log(`${roomId} 연결 해제 완료`);
                });
            } else {
                if (dispatch) {
                    store.dispatch(websocketActions.disconnectSuccess({ roomId }));
                }
            }
        } catch (error) {
            console.error(`${roomId} 연결 해제 오류:`, error);
            if (dispatch) {
                store.dispatch(websocketActions.disconnectSuccess({ roomId }));
            }
        }

        // 재연결 타임아웃 취소
        if (this.reconnectTimeouts[roomId]) {
            clearTimeout(this.reconnectTimeouts[roomId]);
            delete this.reconnectTimeouts[roomId];
        }

        delete this.stompClients[roomId];
        delete this.connectionIds[roomId];
    }

    // 재연결 스케줄링
    scheduleReconnect(roomId, attempt = 1) {
        const MAX_RECONNECT_ATTEMPTS = 10;
        const BASE_DELAY = 1000;
        const MAX_DELAY = 30000;

        if (attempt > MAX_RECONNECT_ATTEMPTS) {
            console.error(`${roomId} 최대 재연결 시도 횟수 초과`);
            return;
        }

        // 지수 백오프 + 랜덤 지터
        const delay = Math.min(
            BASE_DELAY * Math.pow(1.5, attempt - 1) * (0.9 + Math.random() * 0.2),
            MAX_DELAY
        );

        console.log(`${roomId} ${delay.toFixed(0)}ms 후 재연결 시도 (${attempt}/${MAX_RECONNECT_ATTEMPTS})`);

        this.reconnectTimeouts[roomId] = setTimeout(() => {
            this.reconnectTimeouts[roomId] = null;

            // 재연결 시도
            this.connect(roomId).catch(() => {
                // 실패 시 다음 재연결 예약
                if (!this.isConnected(roomId)) {
                    this.scheduleReconnect(roomId, attempt + 1);
                }
            });
        }, delay);
    }

    // 연결 상태 확인
    isConnected(roomId) {
        return this.stompClients[roomId] && this.stompClients[roomId].connected;
    }

    // 모든 연결 정리
    cleanupAll() {
        Object.keys(this.stompClients).forEach(roomId => {
            this.disconnect(roomId);
        });
    }
}

// 싱글톤 인스턴스 생성
export const webSocketService = new WebSocketService();